import type { SystemId } from '../types';

/**
 * チートコードの解析。
 *
 * libretro のコアは `retro_cheat_set(index, enabled, code)` に**生の文字列**を
 * 渡す仕様で、解析はコア側が行う。ここで解析するのは 2 つの理由から:
 *
 *  1. 打ち間違いをコアへ渡す前に弾く。コアによっては不正なコードで
 *     メモリを壊したり黙って無視したりして、原因が分からなくなる。
 *  2. 書き換わるアドレスを UI に出す。効かないコードを調べるときの手がかりになる。
 *
 * NES / GB の Game Genie は仕様が明確なので復号まで行う。
 * SNES の Game Genie はビット並べ替えの資料に揺れがあるため、
 * **推測で実装せず**書式の検証だけに留めている（コアが解析するので実害はない）。
 */

export type CheatFormat =
  | 'game-genie-nes'
  | 'game-genie-gb'
  | 'game-genie-snes'
  | 'raw' // AAAAAA:VV 形式（GameShark / Pro Action Replay 系）
  | 'unknown';

export interface DecodedCheat {
  /** 書き換わるアドレス。復号できない書式では undefined。 */
  address?: number;
  /** 書き込む値 */
  value?: number;
  /** 一致したときだけ書き換える比較値（Game Genie の 8 文字形式など） */
  compare?: number;
}

export interface ParsedCheat {
  /** コアへ渡す正規化済み文字列 */
  normalized: string;
  format: CheatFormat;
  valid: boolean;
  /** valid が false のときの理由 */
  error?: string;
  decoded?: DecodedCheat;
}

/* -------------------------------------------------------------------------- */
/* NES Game Genie                                                             */
/* -------------------------------------------------------------------------- */

const NES_ALPHABET = 'APZLGITYEOXUKSVN';

function decodeNesGameGenie(code: string): DecodedCheat | null {
  const n: number[] = [];
  for (const ch of code) {
    const i = NES_ALPHABET.indexOf(ch);
    if (i < 0) return null;
    n.push(i);
  }
  if (n.length !== 6 && n.length !== 8) return null;

  const a = (i: number) => n[i] ?? 0;

  const address =
    0x8000 +
    (((a(3) & 7) << 12) |
      ((a(5) & 7) << 8) |
      ((a(4) & 8) << 8) |
      ((a(2) & 7) << 4) |
      ((a(1) & 8) << 4) |
      (a(4) & 7) |
      (a(3) & 8));

  if (n.length === 6) {
    const value = ((a(1) & 7) << 4) | ((a(0) & 8) << 4) | (a(0) & 7) | (a(5) & 8);
    return { address, value };
  }

  const value = ((a(1) & 7) << 4) | ((a(0) & 8) << 4) | (a(0) & 7) | (a(7) & 8);
  const compare = ((a(7) & 7) << 4) | ((a(6) & 8) << 4) | (a(6) & 7) | (a(5) & 8);
  return { address, value, compare };
}

/* -------------------------------------------------------------------------- */
/* Game Boy Game Genie                                                        */
/* -------------------------------------------------------------------------- */

function decodeGbGameGenie(code: string): DecodedCheat | null {
  const d: number[] = [];
  for (const ch of code) {
    const v = parseInt(ch, 16);
    if (Number.isNaN(v)) return null;
    d.push(v);
  }
  if (d.length !== 6 && d.length !== 9) return null;

  const g = (i: number) => d[i] ?? 0;

  const value = (g(0) << 4) | g(1);
  // 6 桁目を反転したものがアドレス上位ニブルになる
  const address = ((g(5) ^ 0xf) << 12) | (g(2) << 8) | (g(3) << 4) | g(4);

  if (d.length === 6) return { address, value };

  // 7 桁目と 9 桁目から比較値を作る。右 2 ビット回転して 0xBA と XOR。
  let compare = (g(6) << 4) | g(8);
  compare = ((compare >> 2) | ((compare & 0x03) << 6)) & 0xff;
  compare ^= 0xba;

  return { address, value, compare };
}

/* -------------------------------------------------------------------------- */
/* 生アドレス形式（GameShark / Pro Action Replay 系）                          */
/* -------------------------------------------------------------------------- */

const RAW_RE = /^([0-9A-F]{4,8})[:+]?([0-9A-F]{2,4})$/;

interface RawParts {
  addressText: string;
  valueText: string;
  decoded: DecodedCheat;
}

function decodeRaw(code: string): RawParts | null {
  const m = RAW_RE.exec(code);
  if (!m) return null;
  const addressText = m[1]!;
  const valueText = m[2]!;
  return {
    addressText,
    valueText,
    decoded: { address: parseInt(addressText, 16), value: parseInt(valueText, 16) },
  };
}

/**
 * コアへ渡すときにアドレスと値を空白で区切る必要がある機種。
 *
 * PS1 (pcsx_rearmed) は retro_cheat_set で
 *
 *     sscanf(line, "%x %x", &addr, &val)
 *
 * と読んでいる（libpcsxcore/cheat.c）。区切りが無いと 1 個の巨大な
 * 16 進数として読まれ、値が取れずコードごと捨てられる。
 * libretro 側の shim も「16 進以外の文字を区切りへ置き換える」実装なので、
 * **区切りを消してはいけない**。
 *
 * 「8007C77A A4A0」がまさにこれで効かなかった。こちらが空白を
 * 削って「8007C77AA4A0」にしてコアへ渡していた。
 *
 * 他機種のコアがどう解析しているかは確認できていないので、
 * 裏を取れた PS1 だけに限定してある。同じ症状が出たら足す。
 */
const SPACE_SEPARATED_RAW: ReadonlySet<SystemId> = new Set<SystemId>(['psx']);

/* -------------------------------------------------------------------------- */
/* 判定                                                                        */
/* -------------------------------------------------------------------------- */

/** 表示・入力の揺れを吸収する（小文字、ハイフン、空白） */
export function normalizeCode(input: string): string {
  return input.toUpperCase().replace(/[\s-]/g, '');
}

export function parseCheat(input: string, system: SystemId): ParsedCheat {
  const normalized = normalizeCode(input);

  if (normalized.length === 0) {
    return { normalized, format: 'unknown', valid: false, error: 'コードが空です' };
  }

  // --- NES Game Genie ---
  if (system === 'nes' && /^[APZLGITYEOXUKSVN]+$/.test(normalized)) {
    if (normalized.length !== 6 && normalized.length !== 8) {
      return {
        normalized,
        format: 'game-genie-nes',
        valid: false,
        error: 'NES の Game Genie は 6 文字か 8 文字です',
      };
    }
    const decoded = decodeNesGameGenie(normalized);
    return decoded
      ? { normalized, format: 'game-genie-nes', valid: true, decoded }
      : { normalized, format: 'game-genie-nes', valid: false, error: 'コードを解析できません' };
  }

  // --- Game Boy Game Genie ---
  if (system === 'gb' && /^[0-9A-F]+$/.test(normalized) && (normalized.length === 6 || normalized.length === 9)) {
    const decoded = decodeGbGameGenie(normalized);
    return decoded
      ? { normalized, format: 'game-genie-gb', valid: true, decoded }
      : { normalized, format: 'game-genie-gb', valid: false, error: 'コードを解析できません' };
  }

  // --- SNES Game Genie（書式の検証のみ） ---
  if (system === 'snes' && /^[DF4709156BC8A23E]{8}$/.test(normalized)) {
    // 復号は行わない。ビット並べ替えの資料に揺れがあり、
    // 推測で実装すると「効かない理由」を誤って表示することになる。
    return { normalized, format: 'game-genie-snes', valid: true };
  }

  // --- 生アドレス形式 ---
  const raw = decodeRaw(normalized);
  if (raw) {
    // コアが区切りを要求する機種では、正規化後も区切りを残す
    const text = SPACE_SEPARATED_RAW.has(system)
      ? `${raw.addressText} ${raw.valueText}`
      : normalized;
    return { normalized: text, format: 'raw', valid: true, decoded: raw.decoded };
  }

  return {
    normalized,
    format: 'unknown',
    valid: false,
    error: 'この機種で使える書式ではありません',
  };
}

/** 複数行のコードをまとめて解析する（1 つのチートが複数行になることがある） */
export function parseCheatLines(input: string, system: SystemId): ParsedCheat[] {
  return input
    .split(/[\n,]/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => parseCheat(line, system));
}

/** この機種で受け付ける書式の説明（UI のヒント用） */
export function cheatFormatHint(system: SystemId): string {
  switch (system) {
    case 'nes':
      return 'Game Genie (SXIOPO / GXXZZLVI) または アドレス:値 (00C0:63)';
    case 'gb':
      return 'Game Genie (00A-17B-C49) または アドレス:値 (C0A0:63)';
    case 'snes':
      return 'Game Genie (DD82-64DC) または アドレス:値 (7E0DB4:63)';
    case 'gba':
      return 'GameShark / CodeBreaker 形式 (02000000:0063)';
    case 'psx':
      return 'GameShark 形式 (8007C77A A4A0 / 8009A7D2:0063)。複数行はそのまま貼れます';
    case 'ws':
      return 'アドレス:値 (例 C000:63)';
    case 'psp':
      return 'アドレス:値（対応コア依存）';
  }
}
